/**
 * Nácar · interacción de la tienda en Shopify (sin dependencias).
 * - Carrito REAL: Ajax Cart API (/cart/add.js, /cart/change.js) + Section Rendering API.
 * - Buscador: Predictive Search API.
 * - Comparador antes/después, galería, animaciones y componentes del diseño original.
 */
(() => {
  'use strict';

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const routes = (window.Nacar && window.Nacar.routes) || {};
  const DRAWER_SECTION = 'nacar-cart-drawer';
  const on = (root, type, selector, handler, options) =>
    root.addEventListener(
      type,
      (event) => {
        const target = event.target.closest?.(selector);
        if (target && root.contains(target)) handler(event, target);
      },
      options,
    );
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fold = (s) => String(s).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const store = {
    get(key, fallback = null) {
      try {
        const v = window.localStorage.getItem(key);
        return v === null ? fallback : JSON.parse(v);
      } catch {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* almacenamiento no disponible */
      }
    },
  };

  const ICONS = {
    'check-circle': '<circle cx="12" cy="12" r="9"/><path d="m8 12.5 3 3 5-6"/>',
    'x-circle': '<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>',
    alert: '<path d="M10.3 4.2 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z"/><path d="M12 10v4M12 17h.01"/>',
    close: '<path d="M6 6l12 12M18 6 6 18"/>',
    'chevron-left': '<path d="m15 6-6 6 6 6"/>',
    'chevron-right': '<path d="m9 6 6 6-6 6"/>',
  };
  const icon = (name, size = 20) =>
    `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[name] || ICONS.info}</svg>`;

  /* ---------- Bloqueo de scroll ---------- */
  let locks = 0;
  const lockScroll = () => {
    if (locks === 0) {
      document.documentElement.style.setProperty('--scrollbar-w', `${window.innerWidth - document.documentElement.clientWidth}px`);
      document.documentElement.classList.add('is-locked');
    }
    locks += 1;
  };
  const unlockScroll = () => {
    locks = Math.max(0, locks - 1);
    if (locks === 0) document.documentElement.classList.remove('is-locked');
  };

  /* ---------- Diálogos nativos (menú, buscador, carrito, lightbox) ---------- */
  function openDialog(dialog, { opener = document.activeElement } = {}) {
    if (!dialog || dialog.open) return;
    dialog._opener = opener;
    if (!dialog._wired) {
      dialog._wired = true;
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-close-dialog]')) closeDialog(dialog);
      });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });
    }
    dialog.classList.remove('is-closing');
    dialog.showModal();
    lockScroll();
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  }
  function closeDialog(dialog, { immediate = false } = {}) {
    if (!dialog?.open || (dialog.classList.contains('is-closing') && !immediate)) return;
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
      unlockScroll();
      const opener = dialog._opener;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      dialog.dispatchEvent(new CustomEvent('dialog:close'));
    };
    if (immediate || reducedMotion()) return finish();
    dialog.classList.add('is-closing');
    window.setTimeout(() => dialog.open && finish(), 220);
  }

  /* ---------- Avisos accesibles y toasts ---------- */
  function announce(message) {
    const region = document.getElementById('sr-announcer');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => (region.textContent = message), 50);
  }
  function toastHost() {
    const open = qsa('dialog[open]');
    if (!open.length) return document.getElementById('toasts');
    const parent = open[open.length - 1];
    let host = parent.querySelector(':scope > .toasts');
    if (!host) {
      host = document.createElement('div');
      host.className = 'toasts';
      parent.appendChild(host);
    }
    return host;
  }
  function toast(message, { type = 'info', action = null, duration = 4500 } = {}) {
    const host = toastHost();
    if (!host) return;
    const map = { success: 'check-circle', error: 'x-circle', info: 'info', warning: 'alert' };
    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.setAttribute('role', type === 'error' ? 'alert' : 'status');
    el.innerHTML = `<span class="toast__icon">${icon(map[type], 20)}</span><div class="toast__body"><span>${esc(message)}</span></div>${
      action ? `<button type="button" class="toast__action">${esc(action.label)}</button>` : ''
    }<button type="button" class="toast__close" aria-label="Cerrar aviso">${icon('close', 16)}</button>`;
    host.appendChild(el);
    let timer = null;
    const dismiss = () => {
      window.clearTimeout(timer);
      el.classList.add('is-leaving');
      window.setTimeout(() => el.remove(), reducedMotion() ? 0 : 200);
    };
    const start = () => duration > 0 && (timer = window.setTimeout(dismiss, duration));
    el.querySelector('.toast__close').addEventListener('click', dismiss);
    el.querySelector('.toast__action')?.addEventListener('click', () => {
      action.onClick?.();
      dismiss();
    });
    el.addEventListener('mouseenter', () => window.clearTimeout(timer));
    el.addEventListener('mouseleave', start);
    start();
  }

  /* ---------- Aparición al hacer scroll ---------- */
  let revealObserver = null;
  const revealInView = () => {
    const limit = window.innerHeight * 0.95;
    qsa('.reveal:not(.is-visible)').forEach((el) => el.getBoundingClientRect().top < limit && el.classList.add('is-visible'));
  };
  function observeReveal(root = document) {
    const items = qsa('.reveal:not(.is-visible)', root);
    if (!items.length) return;
    if (reducedMotion() || window.Nacar?.designMode || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    window.setTimeout(revealInView, 450);
    revealObserver ||= new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    items.forEach((el) => revealObserver.observe(el));
  }
  let revealTimer = null;
  window.addEventListener('scroll', () => (revealTimer ||= window.setTimeout(() => ((revealTimer = null), revealInView()), 120)), { passive: true });

  /* ---------- Barra de avisos ---------- */
  function initAnnouncement(bar) {
    if (bar._ready) return;
    bar._ready = true;
    const msgs = qsa('.announcement__msg', bar);
    const every = Number(bar.dataset.rotate) || 5000;
    let index = 0;
    let paused = false;
    let timer = null;
    const show = (i) => {
      index = (i + msgs.length) % msgs.length;
      msgs.forEach((m, k) => {
        m.classList.toggle('is-active', k === index);
        m.setAttribute('aria-hidden', String(k !== index));
        m.querySelectorAll('a').forEach((a) => (a.tabIndex = k === index ? 0 : -1));
      });
    };
    const schedule = () => {
      window.clearInterval(timer);
      if (msgs.length > 1) timer = window.setInterval(() => !paused && !document.hidden && show(index + 1), every);
    };
    bar.addEventListener('mouseenter', () => (paused = true));
    bar.addEventListener('mouseleave', () => (paused = false));
    bar.addEventListener('focusin', () => (paused = true));
    bar.addEventListener('focusout', () => (paused = false));
    qs('[data-ann="prev"]', bar)?.addEventListener('click', () => (show(index - 1), schedule()));
    qs('[data-ann="next"]', bar)?.addEventListener('click', () => (show(index + 1), schedule()));
    qs('.announcement__close', bar)?.addEventListener('click', () => {
      try {
        window.localStorage.setItem('nacar:announcement-dismissed', '1');
      } catch {
        /* sin almacenamiento */
      }
      window.clearInterval(timer);
      bar.classList.add('is-leaving');
      window.setTimeout(() => (bar.hidden = true), 220);
    });
    schedule();
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const header = qs('[data-header]');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      const hideable = window.innerWidth < 1024 && y > 320 && !header.contains(document.activeElement);
      if (hideable && y > lastY + 6) header.classList.add('is-hidden');
      else if (y < lastY - 6 || y <= 320) header.classList.remove('is-hidden');
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => !ticking && ((ticking = true), window.requestAnimationFrame(onScroll)), { passive: true });
    onScroll();
    if ('ResizeObserver' in window) {
      new ResizeObserver(([entry]) => document.documentElement.style.setProperty('--header-h', `${Math.round(entry.contentRect.height)}px`)).observe(header);
    }
    const path = window.location.pathname;
    qsa('.header__nav a.nav-link, .mobile-nav a').forEach((a) => {
      const url = new URL(a.href, window.location.origin);
      if (!url.hash && (url.pathname === path || (url.pathname !== '/' && path.startsWith(`${url.pathname}/`)))) a.setAttribute('aria-current', 'page');
    });
  }

  /* ---------- Carrito (Ajax Cart API + Section Rendering API) ---------- */
  let queue = Promise.resolve();
  const enqueue = (task) => (queue = queue.then(task, task));

  async function cartFetch(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data.description || data.message || 'No hemos podido actualizar el carrito.');
      error.status = res.status;
      throw error;
    }
    return data;
  }
  const cartSections = () => {
    const ids = [DRAWER_SECTION];
    const page = qs('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  };
  function updateCount(count) {
    qsa('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.dataset.count = count;
    });
    qsa('[data-cart-button]').forEach((btn) => {
      btn.setAttribute('aria-label', `Carrito, ${count} ${count === 1 ? 'producto' : 'productos'}`);
      btn.classList.remove('is-bumped');
      void btn.offsetWidth;
      btn.classList.add('is-bumped');
    });
  }
  function renderCartSections(sections) {
    if (!sections) return;
    const active = document.activeElement;
    const row = active?.closest?.('[data-line-row]');
    const focusKey = row?.dataset.lineRow;
    const focusSel = active?.dataset?.qtyStep ? `[data-qty-step="${active.dataset.qtyStep}"]` : active?.classList?.contains('qty__input') ? '.qty__input' : null;
    const drawer = qs('#cart-drawer');
    if (drawer && sections[DRAWER_SECTION]) {
      const doc = new DOMParser().parseFromString(sections[DRAWER_SECTION], 'text/html');
      ['[data-cart-body]', '[data-cart-foot]', '[data-cart-drawer-count]'].forEach((sel) => {
        const fresh = qs(sel, doc);
        const current = qs(sel, drawer);
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          current.hidden = fresh.hidden;
        }
      });
    }
    const page = qs('[data-cart-page]');
    if (page && sections[page.dataset.sectionId]) {
      const doc = new DOMParser().parseFromString(sections[page.dataset.sectionId], 'text/html');
      const fresh = qs('[data-cart-page]', doc);
      if (fresh) page.innerHTML = fresh.innerHTML;
    }
    if (focusKey) {
      const target = qsa(`[data-line-row="${CSS.escape(focusKey)}"]`).find((r) => r.offsetParent !== null);
      const el = (focusSel && target?.querySelector(`${focusSel}:not(:disabled)`)) || target?.querySelector('.qty__input');
      el?.focus({ preventScroll: true });
    }
  }
  async function reloadCartSections() {
    const res = await fetch(`${window.location.pathname}?sections=${cartSections().join(',')}`);
    if (res.ok) renderCartSections(await res.json());
    const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    updateCount(cart.item_count);
  }
  function openCart(opener) {
    const drawer = qs('#cart-drawer');
    if (!drawer) {
      window.location.href = routes.cart;
      return;
    }
    openDialog(drawer, { opener });
    window.requestAnimationFrame(() => qs('[data-cart-body]', drawer)?.focus({ preventScroll: true }));
  }
  function setLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    if (loading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }
  function flyToCart(from) {
    const target = qs('[data-cart-button]');
    if (!from || !target || reducedMotion() || !from.getBoundingClientRect) return;
    const a = from.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    if (!a.width || !b.width) return;
    const dot = document.createElement('span');
    dot.className = 'fly-dot';
    dot.style.left = `${a.left + a.width / 2 - 8}px`;
    dot.style.top = `${a.top + a.height / 2 - 8}px`;
    document.body.appendChild(dot);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    dot
      .animate(
        [
          { transform: 'translate(0,0) scale(1)', opacity: 1 },
          { transform: `translate(${dx * 0.5}px, ${dy - 80}px) scale(1.1)`, opacity: 1, offset: 0.5 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.4)`, opacity: 0.2 },
        ],
        { duration: 620, easing: 'cubic-bezier(.5,0,.3,1)' },
      )
      .finished.finally(() => dot.remove());
  }

  /** Añade la variante REAL del formulario de producto de Shopify. */
  function addToCart(form, { button = null, openDrawer = true, goCheckout = false } = {}) {
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Math.max(1, Math.floor(Number(data.get('quantity')) || 1));
    if (!id) return Promise.resolve();
    const item = { id, quantity };
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) (item.properties ||= {})[match[1]] = value;
    }
    setLoading(button, true);
    return enqueue(async () => {
      try {
        const result = await cartFetch(`${routes.cartAdd}.js`, { items: [item], sections: cartSections().join(','), sections_url: window.location.pathname });
        if (goCheckout) {
          window.location.href = '/checkout';
          return;
        }
        renderCartSections(result.sections);
        const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        updateCount(cart.item_count);
        const title = result.items?.[0]?.product_title || 'El kit';
        announce(`${title} añadido al carrito`);
        if (button && document.contains(button)) {
          const label = button.querySelector('.btn__label');
          const previous = label?.textContent;
          button.classList.add('is-success');
          if (label) label.textContent = 'Añadido';
          window.setTimeout(() => {
            button.classList.remove('is-success');
            if (label) label.textContent = previous;
          }, 1400);
        }
        flyToCart(button);
        const insideDrawer = Boolean(button?.closest?.('#cart-drawer'));
        if (openDrawer && !insideDrawer) window.setTimeout(() => openCart(button), reducedMotion() ? 0 : 380);
      } catch (error) {
        toast(error.message, { type: 'warning' });
      } finally {
        setLoading(button, false);
      }
    });
  }

  function changeLine(key, quantity) {
    const row = qsa(`[data-line-row="${CSS.escape(key)}"]`);
    row.forEach((r) => r.classList.add('is-busy'));
    return enqueue(async () => {
      try {
        const cart = await cartFetch(`${routes.cartChange}.js`, { id: key, quantity, sections: cartSections().join(','), sections_url: window.location.pathname });
        renderCartSections(cart.sections);
        updateCount(cart.item_count);
      } catch (error) {
        toast(error.message, { type: 'warning' });
        await reloadCartSections().catch(() => {});
      } finally {
        row.forEach((r) => r.classList.remove('is-busy'));
      }
    });
  }

  function initCart() {
    on(document, 'click', '[data-action="open-cart"]', (event, el) => {
      if (!qs('#cart-drawer')) return;
      event.preventDefault();
      openCart(el);
    });
    on(document, 'submit', 'form[data-nacar-add-form]', (event, form) => {
      event.preventDefault();
      const button = event.submitter || qs('[type="submit"]', form);
      if (button?.disabled) return;
      addToCart(form, { button });
    });
    on(document, 'click', '[data-remove-line]', (event, el) => {
      const { removeLine: key, variantId, qty, title } = el.dataset;
      changeLine(key, 0).then(() => {
        announce(`${title} eliminado del carrito`);
        toast(`Has eliminado ${title}.`, {
          type: 'info',
          duration: 8000,
          action: {
            label: 'Deshacer',
            onClick: () =>
              enqueue(async () => {
                const result = await cartFetch(`${routes.cartAdd}.js`, { items: [{ id: Number(variantId), quantity: Number(qty) || 1 }], sections: cartSections().join(','), sections_url: window.location.pathname });
                renderCartSections(result.sections);
                const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
                updateCount(cart.item_count);
                announce(`${title} ha vuelto al carrito`);
              }).catch((error) => toast(error.message, { type: 'warning' })),
          },
        });
      });
    });
    // El checkout del carrito siempre es el de Shopify: solo marcamos el estado de carga.
    on(document, 'submit', 'form[action$="/cart"]', (event) => setLoading(event.submitter, true));
  }

  /* ---------- Selectores de cantidad ---------- */
  const setStepperState = (wrap, value) => {
    const input = qs('.qty__input', wrap);
    const min = Number(input.min) || 1;
    const max = Number(input.max) || 99;
    const minus = qs('[data-qty-step="-1"]', wrap);
    const plus = qs('[data-qty-step="1"]', wrap);
    if (minus) minus.disabled = input.disabled || value <= min;
    if (plus) plus.disabled = input.disabled || value >= max;
  };
  function initQuantity() {
    on(document, 'click', '[data-qty-step]', (event, el) => {
      const wrap = el.closest('.qty');
      const input = qs('.qty__input', wrap);
      const step = Number(el.dataset.qtyStep);
      const line = wrap.dataset.line;
      if (line) {
        event.preventDefault();
        changeLine(line, Math.max(0, (Number(input.value) || 0) + step));
        return;
      }
      const min = Number(input.min) || 1;
      const max = Number(input.max) || 99;
      const next = Math.min(max, Math.max(min, (Number(input.value) || min) + step));
      input.value = next;
      setStepperState(wrap, next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    on(document, 'change', '.qty__input', (event, input) => {
      const wrap = input.closest('.qty');
      if (!wrap) return;
      const max = Number(input.max) || 99;
      let value = Math.floor(Number(input.value));
      if (wrap.dataset.line) {
        if (!Number.isFinite(value)) value = 1;
        changeLine(wrap.dataset.line, Math.max(0, Math.min(value, max)));
        return;
      }
      const min = Number(input.min) || 1;
      const clamped = Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
      if (String(clamped) !== input.value) input.value = clamped;
      setStepperState(wrap, clamped);
    });
  }

  /* ---------- Comparador antes / después ---------- */
  /**
   * Pointer events (ratón, táctil y lápiz) + teclado (input range).
   * Solo se mueve el divisor: las imágenes son fijas, no arrastrables y no reciben eventos.
   * Táctil: desde el divisor arrastra al instante; desde otro punto, solo si el gesto es horizontal
   * (así el scroll vertical de la página sigue funcionando).
   */
  function initBeforeAfter(fig) {
    if (fig.dataset.baReady) return;
    fig.dataset.baReady = 'true';
    const stage = qs('.ba__stage', fig);
    const range = qs('.ba__range', fig);
    let pos = Number(range.value) || 50;
    let pending = null;
    let frame = 0;
    let pointerId = null;
    let armed = false;
    let startX = 0;

    const paint = () => {
      frame = 0;
      if (pending == null) return;
      pos = pending;
      pending = null;
      fig.style.setProperty('--pos', `${pos}%`);
      range.value = String(Math.round(pos));
      range.setAttribute('aria-valuetext', `${Math.round(pos)} % de la imagen «antes»`);
    };
    const set = (value) => {
      pending = Math.max(0, Math.min(100, value));
      if (!frame) frame = window.requestAnimationFrame(paint);
    };
    const toPct = (clientX) => {
      const r = stage.getBoundingClientRect();
      return ((clientX - r.left) / r.width) * 100;
    };
    const nearHandle = (clientX) => (Math.abs(toPct(clientX) - pos) * stage.getBoundingClientRect().width) / 100 < 32;
    const begin = (event) => {
      pointerId = event.pointerId;
      armed = false;
      fig.classList.add('is-dragging');
      try {
        stage.setPointerCapture(event.pointerId);
      } catch {
        /* el puntero ya no está activo */
      }
      set(toPct(event.clientX));
    };
    const end = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      armed = false;
      fig.classList.remove('is-dragging');
      if (stage.hasPointerCapture?.(event.pointerId)) stage.releasePointerCapture(event.pointerId);
    };

    qsa('img', stage).forEach((img) => (img.draggable = false));
    stage.addEventListener('dragstart', (event) => event.preventDefault());
    stage.addEventListener('selectstart', (event) => event.preventDefault());
    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      if (event.pointerType === 'mouse' || nearHandle(event.clientX)) {
        begin(event);
        return;
      }
      pointerId = event.pointerId;
      armed = true;
      startX = event.clientX;
    });
    stage.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      if (armed) {
        if (Math.abs(event.clientX - startX) > 6) begin(event);
        return;
      }
      set(toPct(event.clientX));
    });
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
    stage.addEventListener('lostpointercapture', end);
    range.addEventListener('input', () => set(Number(range.value)));
  }

  /* ---------- Galería de la ficha + lightbox ---------- */
  function initGallery(gallery) {
    if (!gallery || gallery._ready) return;
    gallery._ready = true;
    const track = qs('[data-gallery-track]', gallery);
    const slides = [...track.children];
    const thumbs = qsa('[data-gallery-go]', gallery);
    const thumbList = qs('.gallery__thumbs', gallery);
    const counter = qs('[data-gallery-counter]', gallery);
    let index = 0;
    const setActive = (i) => {
      if (i === index && thumbs[i]?.classList.contains('is-active')) return;
      index = i;
      thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === i);
        t.setAttribute('aria-current', String(k === i));
      });
      if (counter) counter.textContent = `${i + 1} / ${slides.length}`;
      const thumb = thumbs[i];
      if (thumb && thumbList) {
        const vertical = thumbList.scrollHeight > thumbList.clientHeight + 4;
        if (vertical) thumbList.scrollTo({ top: thumb.offsetTop - thumbList.clientHeight / 2 + thumb.clientHeight / 2, behavior: 'smooth' });
        else thumbList.scrollTo({ left: thumb.offsetLeft - thumbList.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
      }
    };
    const goTo = (i) => {
      const target = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      setActive(target);
    };
    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          setActive(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
          ticking = false;
        });
      },
      { passive: true },
    );
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') (event.preventDefault(), goTo(index + 1));
      else if (event.key === 'ArrowLeft') (event.preventDefault(), goTo(index - 1));
    });
    gallery.addEventListener('click', (event) => {
      const step = event.target.closest('[data-gallery-step]');
      const go = event.target.closest('[data-gallery-go]');
      const open = event.target.closest('[data-gallery-open]');
      if (step) goTo(index + Number(step.dataset.galleryStep));
      else if (go) goTo(Number(go.dataset.galleryGo));
      else if (open) openLightbox(gallery, open.dataset.galleryOpen === 'current' ? index : Number(open.dataset.galleryOpen), open);
    });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      qsa('.gallery__zoomable', gallery).forEach((btn) => {
        const img = qs('img', btn);
        if (!img) return;
        btn.classList.add('can-zoom');
        btn.addEventListener('pointermove', (event) => {
          const r = btn.getBoundingClientRect();
          img.style.transformOrigin = `${((event.clientX - r.left) / r.width) * 100}% ${((event.clientY - r.top) / r.height) * 100}%`;
          img.style.transform = 'scale(1.9)';
        });
        btn.addEventListener('pointerleave', () => (img.style.transform = ''));
      });
    }
  }

  function openLightbox(gallery, start, opener) {
    document.getElementById('lightbox')?.remove();
    const items = qsa('.gallery__zoomable', gallery).map((btn) => ({ src: btn.dataset.full, alt: btn.dataset.alt || '', ratio: qs('.media', btn)?.style.getPropertyValue('--ratio') || '1/1' }));
    const n = items.length;
    const title = qs('.pinfo__title')?.textContent.trim() || 'Galería';
    const media = (m, fit) => `<div class="media" style="--ratio:${esc(m.ratio)};--fit:${fit}"><img src="${esc(m.src)}" alt="${esc(m.alt)}" draggable="false" loading="lazy"></div>`;
    document.getElementById('overlays').insertAdjacentHTML(
      'beforeend',
      `<dialog class="dialog lightbox" id="lightbox" aria-label="${esc(title)}: galería a pantalla completa"><div class="lightbox__panel">
        <div class="lightbox__top"><p class="lightbox__title">${esc(title)} <span data-lb-counter>${start + 1} / ${n}</span></p><button type="button" class="icon-btn lightbox__close" data-close-dialog aria-label="Cerrar galería">${icon('close')}</button></div>
        <ul role="list" class="lightbox__track no-scrollbar" data-lb-track tabindex="0">${items.map((m, i) => `<li class="lightbox__slide" aria-label="${i + 1} de ${n}"><div class="lightbox__img" data-lb-zoom>${media(m, 'contain')}</div></li>`).join('')}</ul>
        ${
          n > 1
            ? `<button type="button" class="lightbox__arrow lightbox__arrow--prev" data-lb-step="-1" aria-label="Anterior">${icon('chevron-left', 26)}</button><button type="button" class="lightbox__arrow lightbox__arrow--next" data-lb-step="1" aria-label="Siguiente">${icon('chevron-right', 26)}</button>
              <ul role="list" class="lightbox__thumbs no-scrollbar">${items.map((m, i) => `<li><button type="button" class="lightbox__thumb${i === start ? ' is-active' : ''}" data-lb-go="${i}" aria-label="Ir al elemento ${i + 1}">${media({ ...m, ratio: '1/1' }, 'cover')}</button></li>`).join('')}</ul>`
            : ''
        }
      </div></dialog>`,
    );
    const dialog = document.getElementById('lightbox');
    const track = qs('[data-lb-track]', dialog);
    const counter = qs('[data-lb-counter]', dialog);
    const lbThumbs = qsa('[data-lb-go]', dialog);
    let index = start;
    const update = (i) => {
      index = i;
      counter.textContent = `${i + 1} / ${n}`;
      lbThumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
      qsa('.lightbox__img.is-zoomed', dialog).forEach((z) => z.classList.remove('is-zoomed'));
    };
    const goTo = (i, smooth = true) => {
      const target = (i + n) % n;
      track.scrollTo({ left: target * track.clientWidth, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
      update(target);
    };
    track.addEventListener('scroll', () => window.requestAnimationFrame(() => {
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (i !== index) update(i);
    }), { passive: true });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') goTo(index + 1);
      if (event.key === 'ArrowLeft') goTo(index - 1);
    });
    dialog.addEventListener('click', (event) => {
      const step = event.target.closest('[data-lb-step]');
      const go = event.target.closest('[data-lb-go]');
      const zoom = event.target.closest('[data-lb-zoom]');
      if (step) goTo(index + Number(step.dataset.lbStep));
      else if (go) goTo(Number(go.dataset.lbGo));
      else if (zoom && qs('img', zoom)) zoom.classList.toggle('is-zoomed');
    });
    dialog.addEventListener('pointermove', (event) => {
      const zoomed = event.target.closest('.lightbox__img.is-zoomed');
      const img = zoomed && qs('img', zoomed);
      if (!img) return;
      const r = zoomed.getBoundingClientRect();
      img.style.transformOrigin = `${((event.clientX - r.left) / r.width) * 100}% ${((event.clientY - r.top) / r.height) * 100}%`;
    });
    dialog.addEventListener('dialog:close', () => dialog.remove(), { once: true });
    openDialog(dialog, { opener });
    window.requestAnimationFrame(() => {
      goTo(start, false);
      qs('.lightbox__close', dialog).focus();
    });
  }

  /* ---------- Ficha del kit ---------- */
  function initProduct(section) {
    if (section._ready) return;
    section._ready = true;
    initGallery(qs('[data-gallery]', section));
    const form = qs('[data-product-form]', section);
    if (!form) return;
    const qtyInput = qs('input[name="quantity"]', form);
    const addBtn = qs('[data-pdp-add]', form);
    const syncPack = () => {
      const qty = Number(qtyInput.value) || 1;
      const radios = qsa('input[name="pack"]', form);
      const tier = radios.map((r) => Number(r.value)).sort((a, b) => b - a).find((q) => qty >= q);
      radios.forEach((r) => (r.checked = tier ? Number(r.value) === tier : false));
    };
    form.addEventListener('change', async (event) => {
      if (event.target.name === 'pack') {
        qtyInput.value = Math.min(Number(event.target.value), Number(qtyInput.max) || 99);
        setStepperState(qtyInput.closest('.qty'), Number(qtyInput.value));
      } else if (event.target === qtyInput) {
        syncPack();
      } else if (event.target.matches('[data-variant-select]')) {
        const id = event.target.value;
        qs('[data-variant-id]', form).value = id;
        const url = new URL(section.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.pathname + url.search);
        url.searchParams.set('section_id', section.dataset.sectionId);
        try {
          const html = await fetch(url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-pinfo-price]', '[data-pinfo-stock]', '[data-bundles]', '[data-pinfo-badges]', '.gallery__badges'].forEach((sel) => {
            const fresh = qs(sel, doc);
            const current = qs(sel, section);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          });
          const freshAdd = qs('[data-pdp-add]', doc);
          if (freshAdd) {
            addBtn.disabled = freshAdd.disabled;
            addBtn.innerHTML = freshAdd.innerHTML;
          }
          const freshBuy = qs('[data-buy-now]', doc);
          const buy = qs('[data-buy-now]', form);
          if (freshBuy && buy) {
            buy.disabled = freshBuy.disabled;
            buy.hidden = freshBuy.hidden;
          }
          const freshNotify = qs('[data-notify]', doc);
          const notify = qs('[data-notify]', section);
          if (freshNotify && notify) notify.hidden = freshNotify.hidden;
          const stickyPrice = qs('[data-sticky-price]');
          const freshSticky = qs('[data-sticky-price]', doc);
          if (stickyPrice && freshSticky) stickyPrice.innerHTML = freshSticky.innerHTML;
          announce(addBtn.disabled ? 'Opción agotada' : 'Opción disponible');
        } catch {
          /* se mantiene el estado anterior */
        }
      }
    });
    qs('[data-buy-now]', form)?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      if (button.disabled) return;
      addToCart(form, { button, openDrawer: false, goCheckout: true });
    });
    const sticky = qs('[data-sticky-atc]');
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.inert = !show;
      }).observe(addBtn);
      qs('[data-sticky-add]', sticky).addEventListener('click', (event) => {
        if (addBtn.disabled) {
          qs('[data-notify]', section)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        addToCart(form, { button: event.currentTarget });
      });
    }
    qs('[data-share]', section)?.addEventListener('click', async (event) => {
      const url = window.location.href;
      try {
        if (navigator.share) await navigator.share({ title: event.currentTarget.dataset.shareTitle, url });
        else {
          await navigator.clipboard.writeText(url);
          toast('Enlace copiado al portapapeles.', { type: 'success', duration: 2500 });
        }
      } catch {
        /* el usuario canceló */
      }
    });
  }

  /* ---------- Centro de ayuda (FAQ) ---------- */
  function initFaqPage(root) {
    if (root._ready) return;
    root._ready = true;
    const input = qs('[data-faq-search]', root);
    const chips = qsa('[data-faq-cat]', root);
    const groups = qsa('[data-faq-group]', root);
    const empty = qs('[data-faq-empty]', root);
    const params = new URLSearchParams(window.location.search);
    let active = chips.some((c) => c.dataset.faqCat && c.dataset.faqCat === params.get('categoria')) ? params.get('categoria') : '';
    const render = () => {
      const q = fold(input.value.trim());
      let visible = 0;
      groups.forEach((group) => {
        let groupVisible = 0;
        qsa('[data-faq-item]', group).forEach((item) => {
          const match = (!active || group.dataset.faqGroup === active) && (!q || fold(item.textContent).includes(q));
          item.hidden = !match;
          if (q && match) item.open = true;
          if (match) groupVisible += 1;
        });
        group.hidden = groupVisible === 0;
        visible += groupVisible;
      });
      empty.hidden = visible > 0;
      chips.forEach((chip) => {
        const on = chip.dataset.faqCat === active;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', String(on));
      });
      return visible;
    };
    input.addEventListener('input', () => announce(`${render()} preguntas encontradas`));
    root.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-faq-cat]');
      if (!chip) return;
      active = chip.dataset.faqCat;
      const url = new URL(window.location.href);
      if (active) url.searchParams.set('categoria', active);
      else url.searchParams.delete('categoria');
      window.history.replaceState({}, '', url);
      render();
    });
    render();
  }

  /* ---------- Formularios nativos: validación accesible antes de enviar ---------- */
  function validateField(field) {
    const value = field.type === 'checkbox' ? field.checked : field.value.trim();
    let message = '';
    if (field.required && !value) message = field.dataset.error || 'Este campo es obligatorio.';
    else if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) message = 'Escribe un email válido.';
    else if (field.minLength > 0 && value && value.length < field.minLength) message = field.dataset.error || `Escribe al menos ${field.minLength} caracteres.`;
    const errorId = (field.getAttribute('aria-describedby') || '').split(' ').find((id) => id.endsWith('-error'));
    const errorEl = errorId && document.getElementById(errorId);
    if (message) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }
    return !message;
  }
  function initForms() {
    on(document, 'submit', 'form[data-validate], form[data-newsletter]', (event, form) => {
      const fields = qsa('input, select, textarea', form).filter((f) => f.required);
      const invalid = fields.filter((f) => !validateField(f));
      if (invalid.length) {
        event.preventDefault();
        invalid[0].focus();
        return;
      }
      setLoading(event.submitter || qs('[type="submit"]', form), true);
    });
    on(document, 'change', 'form[data-validate] [aria-invalid="true"], form[data-newsletter] [aria-invalid="true"]', (event, field) => validateField(field));
    const success = qs('.contact__success');
    if (success) success.focus({ preventScroll: false });
  }

  /* ---------- Buscador predictivo ---------- */
  function initSearch() {
    const dialog = qs('#search-dialog');
    if (!dialog) return;
    const input = qs('#search-input', dialog);
    const form = qs('[data-search-form]', dialog);
    const idle = qs('[data-search-idle]', dialog);
    const results = qs('[data-search-results]', dialog);
    const clear = qs('[data-search-clear]', dialog);
    const recentBox = qs('[data-search-recent]', dialog);
    const recentList = qs('[data-search-recent-list]', dialog);
    let active = -1;
    let timer = null;
    let controller = null;
    const recent = () => store.get('nacar:recent-searches', []);
    const pushRecent = (term) => store.set('nacar:recent-searches', [term, ...recent().filter((t) => t !== term)].slice(0, 6));
    const renderRecent = () => {
      const items = recent();
      recentBox.hidden = !items.length;
      recentList.innerHTML = items.map((t) => `<button type="button" class="chip chip--sm" data-search-term="${esc(t)}">${esc(t)}</button>`).join('');
    };
    const showIdle = () => {
      results.hidden = true;
      results.innerHTML = '';
      idle.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      renderRecent();
    };
    const update = async () => {
      const q = input.value.trim();
      active = -1;
      clear.hidden = !q;
      if (!q) return showIdle();
      controller?.abort();
      controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&section_id=nacar-predictive-search`;
        const html = await fetch(url, { signal: controller.signal }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        results.innerHTML = (qs('.shopify-section', doc) || doc.body).innerHTML;
        idle.hidden = true;
        results.hidden = false;
        const count = qsa('[role="option"]', results).length;
        input.setAttribute('aria-expanded', String(count > 0));
        announce(count ? `${count} sugerencias disponibles` : 'Sin resultados');
      } catch (error) {
        if (error.name !== 'AbortError') showIdle();
      }
    };
    const setActive = (i) => {
      const options = qsa('[role="option"]', results);
      if (!options.length) return;
      active = (i + options.length) % options.length;
      options.forEach((o, k) => {
        o.setAttribute('aria-selected', String(k === active));
        qs('.search-result', o)?.classList.toggle('is-active', k === active);
      });
      input.setAttribute('aria-activedescendant', options[active].id);
      options[active].scrollIntoView({ block: 'nearest' });
    };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(update, 180);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') (event.preventDefault(), setActive(active + 1));
      else if (event.key === 'ArrowUp') (event.preventDefault(), setActive(active - 1));
      else if (event.key === 'Enter' && active >= 0) {
        event.preventDefault();
        const link = qsa('[data-search-result]', results)[active];
        pushRecent(input.value.trim());
        if (link) window.location.href = link.href;
      }
    });
    form.addEventListener('submit', (event) => {
      const q = input.value.trim();
      if (!q) {
        event.preventDefault();
        return;
      }
      pushRecent(q);
    });
    clear.addEventListener('click', () => {
      input.value = '';
      showIdle();
      clear.hidden = true;
      input.focus();
    });
    dialog.addEventListener('click', (event) => {
      const term = event.target.closest('[data-search-term]')?.dataset.searchTerm;
      if (term) {
        input.value = term;
        update();
        input.focus();
      } else if (event.target.closest('[data-search-clear-recent]')) {
        store.set('nacar:recent-searches', []);
        renderRecent();
        input.focus();
      } else if (event.target.closest('[data-search-result], [data-search-all]') && input.value.trim()) {
        pushRecent(input.value.trim());
      }
    });
    on(document, 'click', '[data-action="open-search"]', (event, el) => {
      event.preventDefault();
      const menu = qs('#mobile-menu');
      if (menu?.open) closeDialog(menu, { immediate: true });
      renderRecent();
      openDialog(dialog, { opener: el });
      window.requestAnimationFrame(() => {
        input.focus();
        input.select();
      });
    });
  }

  /* ---------- Varios ---------- */
  function initMisc() {
    on(document, 'click', '[data-action="open-menu"]', (event, el) => openDialog(qs('#mobile-menu'), { opener: el }));
    on(document, 'click', '[data-scroll-top]', (event) => {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
      qs('[data-pdp-add]')?.focus({ preventScroll: true });
    });
    // Preferencias de cookies: banner de privacidad nativo de Shopify (si está activo).
    const showCookiePrefs = () => {
      if (!window.privacyBanner?.showPreferences) return;
      qsa('[data-cookie-prefs]').forEach((li) => (li.hidden = false));
    };
    window.addEventListener('load', () => window.setTimeout(showCookiePrefs, 1200));
    on(document, 'click', '[data-action="open-cookie-settings"]', () => window.privacyBanner?.showPreferences?.());
  }

  /* ---------- Arranque (y recarga de secciones en el editor de temas) ---------- */
  function init(root = document) {
    observeReveal(root);
    qsa('[data-ba]', root).forEach(initBeforeAfter);
    qsa('[data-announcement]', root).forEach(initAnnouncement);
    qsa('[data-product-section]', root).forEach(initProduct);
    qsa('[data-faq-page]', root).forEach(initFaqPage);
  }

  initHeader();
  initCart();
  initQuantity();
  initForms();
  initSearch();
  initMisc();
  init();

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    if (event.target.querySelector('[data-header]')) initHeader();
  });
})();
